import { useCallback, useRef } from 'react';
import { CommonActions } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { useResponsive, useSafeNavigation, useSafeRouteParams } from '@sg/garnish';

import { useIsExpoRouterEnabled } from '@order/shared/hooks';

import { useSafeExpoNavigationReset } from '../../../routing';
import { APP_STACK_NAVIGATOR_ID } from '../../AppNavigation.constants';

import type { AppStackParamList } from '../../AppNavigation.props';

/**
 * Returns a helper function that redirects to the corresponding screen provided
 * as a route for `Auth` related screens.
 */
export const useRedirectAfterAuthStateChange = () => {
  const isExpoRouterEnabled = useIsExpoRouterEnabled();

  const navigation = useSafeNavigation<NativeStackNavigationProp<AppStackParamList>>();
  const resetNavigation = useSafeExpoNavigationReset();
  const { minWidth } = useResponsive();
  const { redirect: redirectToPath = 'home', redirectToGiftCardId } = useSafeRouteParams<{
    redirect?: string;
    redirectToGiftCardId?: string;
  }>();

  // ─── RN Navigation Redirects ──────────────────────────────────────────

  const { current: redirects } = useRef<RedirectHandlers>({
    home() {
      navigation?.navigate('MainTabs', {
        screen: 'HomeTab',
        params: { screen: 'Home' },
        initial: false,
      });
    },
    locations() {
      navigation?.navigate('MainTabs', {
        screen: 'MenuTab',
        params: { screen: 'Locations' },
      });
    },
    profile() {
      navigation?.navigate('MainTabs', {
        screen: 'AccountTab',
        params: { screen: 'Profile', initial: false },
      });
    },
    orders() {
      navigation?.navigate('MainTabs', {
        screen: 'AccountTab',
        params: { screen: 'Orders', initial: false },
      });
    },
    addresses() {
      navigation?.navigate('MainTabs', {
        screen: 'AccountTab',
        params: { screen: 'Addresses', initial: false },
      });
    },
    favorites() {
      navigation?.navigate('MainTabs', {
        screen: 'AccountTab',
        params: { screen: 'Favorites', initial: false },
      });
    },
    'payment-and-gift-cards'() {
      navigation?.navigate('MainTabs', {
        screen: 'AccountTab',
        params: {
          screen: 'PaymentAndGiftCards',
          initial: false,
          params: { giftCardId: redirectToGiftCardId },
        },
      });
    },
    'credit-and-promo-codes'() {
      navigation?.navigate('MainTabs', {
        screen: 'AccountTab',
        params: { screen: 'CreditAndPromoCodes', initial: false },
      });
    },
    'dietary-restrictions'() {
      navigation?.navigate('MainTabs', {
        screen: 'AccountTab',
        params: { screen: 'AccountDietaryRestrictions', initial: false },
      });
    },
    bag() {
      navigation?.navigate('Modal', {
        screen: 'Bag',
      });
    },
    'add-gift-card'() {
      navigation?.navigate('Modal', {
        screen: 'AddGiftCard',
        params: { giftCardId: redirectToGiftCardId },
      });
    },
    'gift-cards'() {
      navigation?.navigate('MainTabs', {
        screen: 'GiftCardsTab',
        params: { screen: 'GiftCards' },
        initial: true,
      });
    },
    'gift-redeem'() {
      if (!redirectToGiftCardId) return;

      navigation?.navigate('MainTabs', {
        screen: 'GiftCardsTab',
        params: {
          screen: 'GiftCardRedemption',
          params: { giftCardId: redirectToGiftCardId },
        },
        initial: false,
      });
    },
    notifications() {
      navigation?.navigate('MainTabs', {
        screen: 'AccountTab',
        params: { screen: 'Notifications', initial: false },
      });
    },
    payments() {
      navigation?.navigate('Modal', {
        screen: 'Payments',
        params: {},
      });
    },
  });

  /**
   * Dismisses `Auth` stack after auth state change (RN navigation only)
   */
  const dismissAuthStack = useCallback(() => {
    const appStackNavigator = navigation?.getParent(APP_STACK_NAVIGATOR_ID as never);

    appStackNavigator?.dispatch((state) => {
      const routesWithoutAuth = withoutAuthStack(state.routes);

      return CommonActions.reset({
        ...state,
        routes: routesWithoutAuth,
        index: routesWithoutAuth.length - 1,
      });
    });
  }, [navigation]);

  // ─────────────────────────────────────────────────────────────────────

  const redirectHandler = redirects[redirectToPath];

  return useCallback(() => {
    if (isExpoRouterEnabled) {
      if (redirectToPath === 'gift-redeem' && !redirectToGiftCardId) return;

      const resetState = getExpoResetState(redirectToPath, redirectToGiftCardId, minWidth.isSM);

      resetNavigation?.(resetState);

      return;
    }

    redirectHandler?.();
    dismissAuthStack();
  }, [
    isExpoRouterEnabled,
    resetNavigation,
    minWidth.isSM,
    redirectToPath,
    redirectToGiftCardId,
    dismissAuthStack,
    redirectHandler,
  ]);
};

// ─── Expo Router Reset Maps ──────────────────────────────────────────────────

const TAB_ROUTE_MAP: Record<string, TabRouteMapping> = {
  home: { tabName: '(home)' },
  locations: { tabName: '(menu)', screenName: 'locations/index' },
  profile: { tabName: 'account', screenName: 'info/index' },
  orders: { tabName: 'account', screenName: 'orders/index' },
  addresses: { tabName: 'account', screenName: 'addresses/index' },
  favorites: { tabName: 'account', screenName: 'favorites/index' },
  'payment-and-gift-cards': { tabName: 'account', screenName: 'payment-and-gift-cards/index' },
  'credit-and-promo-codes': { tabName: 'account', screenName: 'credit-and-promo-codes/index' },
  'dietary-restrictions': { tabName: 'account', screenName: 'dietary-restrictions/index' },
  notifications: { tabName: 'account', screenName: 'notifications/index' },
  'gift-cards': { tabName: 'gift' },
  'gift-redeem': { tabName: 'gift', screenName: 'redeem/[giftCardId]/index' },
};

const MODAL_ROUTE_NAMES: Record<string, string> = {
  bag: 'bag/index',
  'add-gift-card': 'add-gift-card/index',
  payments: 'payments/index',
};

const SM_MODAL_TAB_OVERRIDES: Record<string, TabRouteMapping> = {
  'add-gift-card': { tabName: 'account', screenName: 'gift-cards/index' },
  payments: { tabName: 'account', screenName: 'payment-and-gift-cards/index' },
};

const GIFT_CARD_REDIRECTS = new Set(['payment-and-gift-cards', 'add-gift-card', 'gift-redeem']);

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Builds the reset state for Expo Router redirects after auth state change.
 * Mirrors the legacy router's `dismissAuthStack` by resetting the root stack
 * to exclude `(auth)` and land on the correct target screen.
 */
function getExpoResetState(
  redirectToPath: string,
  giftCardId: string | undefined,
  isSmUp: boolean,
): ExpoResetState {
  const params = getGiftCardParams(redirectToPath, giftCardId);

  // On SM+, redirect modal screens to their tab equivalents.
  const smTabOverride = isSmUp ? SM_MODAL_TAB_OVERRIDES[redirectToPath] : undefined;

  if (smTabOverride) {
    const screenRoute = params
      ? { name: smTabOverride.screenName, params }
      : { name: smTabOverride.screenName };

    return {
      index: 0,
      routes: [
        {
          name: '(tabs)',
          state: { routes: [{ name: smTabOverride.tabName, state: { routes: [screenRoute] } }] },
        },
      ],
    } as ExpoResetState;
  }

  const modalRouteName = MODAL_ROUTE_NAMES[redirectToPath];

  if (modalRouteName) {
    const modalRoute = params ? { name: modalRouteName, params } : { name: modalRouteName };

    return {
      index: 1,
      routes: [
        { name: '(tabs)' as any, state: { routes: [{ name: '(home)' }] } },
        { name: '(modals)' as any, state: { routes: [modalRoute as any] } },
      ],
    };
  }

  const { tabName, screenName } = TAB_ROUTE_MAP[redirectToPath] ?? TAB_ROUTE_MAP.home;

  if (!screenName) {
    return {
      index: 0,
      routes: [{ name: '(tabs)' as any, state: { routes: [{ name: tabName as any }] } }],
    };
  }

  const screenRoute = params ? { name: screenName, params } : { name: screenName };

  return {
    index: 0,
    routes: [
      {
        name: '(tabs)' as any,
        state: { routes: [{ name: tabName as any, state: { routes: [screenRoute as any] } }] },
      },
    ],
  };
}

/**
 * Dismisses `Auth` stack from `MainTabs` stack
 */
function withoutAuthStack(routes: readonly NavigationRoute[]) {
  return routes.filter((route) => route.name !== 'Auth');
}

function getGiftCardParams(
  redirectToPath: string,
  redirectToGiftCardId: string | undefined,
): Record<string, string> | undefined {
  if (!redirectToGiftCardId) return;
  if (!GIFT_CARD_REDIRECTS.has(redirectToPath)) return;

  return { giftCardId: redirectToGiftCardId };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type RedirectHandlers = Record<string, () => void>;

type NavigationRoute = Readonly<{
  name: string;
}>;

type ExpoResetState = Parameters<NonNullable<ReturnType<typeof useSafeExpoNavigationReset>>>[0];

type TabRouteMapping = {
  tabName: string;
  screenName?: string;
};
